/**
 * Permission keys — mirrors the catalogue in swaash-service, one entry per row, so a
 * mistyped key is a compile error rather than a silently hidden button.
 *
 * Add a permission by adding the backend CSV row and the matching entry here.
 */
export const PERMISSIONS = {
  // Users & Roles
  USERS_USER_VIEW: 'users.user.view',
  USERS_USER_CREATE: 'users.user.create',
  USERS_USER_UPDATE: 'users.user.update',
  USERS_USER_ACTIVATE: 'users.user.activate',
  USERS_USER_ARCHIVE: 'users.user.archive',
  USERS_USER_RESEND_INVITE: 'users.user.resend-invite',
  USERS_USER_ASSIGN_ROLE: 'users.user.assign-role',
  USERS_ROLE_VIEW_PERMISSIONS: 'users.role.view-permissions',
  USERS_ROLE_MANAGE_PERMISSIONS: 'users.role.manage-permissions',
  // Clinic & Organization
  CLINIC_CLINIC_VIEW: 'clinic.clinic.view',
  CLINIC_CLINIC_CREATE: 'clinic.clinic.create',
  CLINIC_CLINIC_UPDATE: 'clinic.clinic.update',
  CLINIC_CLINIC_ARCHIVE: 'clinic.clinic.archive',
  CLINIC_LOCATIONS_MANAGE: 'clinic.locations.manage',
  CLINIC_ROOMS_VIEW: 'clinic.rooms.view',
  CLINIC_ROOMS_MANAGE: 'clinic.rooms.manage',
  // Charge Master & Pricing
  CHARGE_MASTER_CHARGE_ITEM_VIEW: 'charge-master.charge-item.view',
  CHARGE_MASTER_CHARGE_ITEM_CREATE: 'charge-master.charge-item.create',
  CHARGE_MASTER_CHARGE_ITEM_UPDATE: 'charge-master.charge-item.update',
  CHARGE_MASTER_CHARGE_ITEM_ARCHIVE: 'charge-master.charge-item.archive',
  CHARGE_MASTER_CHARGE_ITEM_IMPORT: 'charge-master.charge-item.import',
  CHARGE_MASTER_CHARGE_ITEM_EXPORT: 'charge-master.charge-item.export',
  CHARGE_MASTER_DISCOUNT_CODE_VIEW: 'charge-master.discount-code.view',
  CHARGE_MASTER_DISCOUNT_CODE_CREATE: 'charge-master.discount-code.create',
  CHARGE_MASTER_DISCOUNT_CODE_UPDATE: 'charge-master.discount-code.update',
  CHARGE_MASTER_DISCOUNT_CODE_ACTIVATE: 'charge-master.discount-code.activate',
  CHARGE_MASTER_DISCOUNT_CODE_ARCHIVE: 'charge-master.discount-code.archive',
  CHARGE_MASTER_DISCOUNT_CODE_DELETE: 'charge-master.discount-code.delete',
  // Patients
  PATIENTS_PATIENT_VIEW: 'patients.patient.view',
  PATIENTS_PATIENT_OPEN_CHART: 'patients.patient.open-chart',
  PATIENTS_PATIENT_CREATE: 'patients.patient.create',
  PATIENTS_PATIENT_UPDATE: 'patients.patient.update',
  PATIENTS_PATIENT_ASSOCIATE_CLINIC: 'patients.patient.associate-clinic',
  PATIENTS_PATIENT_ACTIVATE: 'patients.patient.activate',
  PATIENTS_PATIENT_ARCHIVE: 'patients.patient.archive',
  PATIENTS_PATIENT_INVITE: 'patients.patient.invite',
  CHART_VITALS_VIEW: 'chart.vitals.view',
  CHART_VITALS_RECORD: 'chart.vitals.record',
  CHART_ALLERGIES_VIEW: 'chart.allergies.view',
  CHART_ALLERGIES_RECORD: 'chart.allergies.record',
  CHART_STICKY_NOTES_CREATE: 'chart.sticky-notes.create',
  CHART_STICKY_NOTES_MANAGE: 'chart.sticky-notes.manage',
  // Documents & Forms
  DOCUMENTS_DOCUMENT_VIEW: 'documents.document.view',
  DOCUMENTS_DOCUMENT_UPLOAD: 'documents.document.upload',
  DOCUMENTS_DOCUMENT_UPDATE: 'documents.document.update',
  DOCUMENTS_DOCUMENT_ARCHIVE: 'documents.document.archive',
  FORMS_TEMPLATES_VIEW: 'forms.templates.view',
  FORMS_TEMPLATES_MANAGE: 'forms.templates.manage',
  FORMS_ASSIGNMENT_ASSIGN: 'forms.assignment.assign',
  FORMS_ASSIGNMENT_EMAIL: 'forms.assignment.email',
  FORMS_SUBMISSIONS_VIEW: 'forms.submissions.view',
  FORMS_SUBMISSIONS_ARCHIVE: 'forms.submissions.archive',
  // Scheduling & Appointments
  AVAILABILITY_AVAILABILITY_VIEW: 'availability.availability.view',
  AVAILABILITY_AVAILABILITY_UPDATE: 'availability.availability.update',
  SCHEDULING_APPOINTMENT_VIEW: 'scheduling.appointment.view',
  SCHEDULING_APPOINTMENT_BOOK: 'scheduling.appointment.book',
  SCHEDULING_APPOINTMENT_UPDATE: 'scheduling.appointment.update',
  SCHEDULING_APPOINTMENT_RESCHEDULE: 'scheduling.appointment.reschedule',
  SCHEDULING_APPOINTMENT_CANCEL: 'scheduling.appointment.cancel',
  SCHEDULING_APPOINTMENT_CHANGE_STATUS: 'scheduling.appointment.change-status',
  SCHEDULING_APPOINTMENT_ARCHIVE: 'scheduling.appointment.archive',
  SCHEDULING_TELEHEALTH_JOIN: 'scheduling.telehealth.join',
  // Encounters & Notes
  ENCOUNTERS_ENCOUNTER_VIEW: 'encounters.encounter.view',
  ENCOUNTERS_ENCOUNTER_START: 'encounters.encounter.start',
  ENCOUNTERS_ENCOUNTER_WRITE: 'encounters.encounter.write',
  ENCOUNTERS_ENCOUNTER_SIGN: 'encounters.encounter.sign',
  ENCOUNTERS_ENCOUNTER_EXPORT: 'encounters.encounter.export',
  ENCOUNTERS_TEMPLATES_APPLY: 'encounters.templates.apply',
  // Plan of Care
  PLAN_OF_CARE_PLAN_VIEW: 'plan-of-care.plan.view',
  PLAN_OF_CARE_PLAN_AUTHOR: 'plan-of-care.plan.author',
  PLAN_OF_CARE_PLAN_LIFECYCLE: 'plan-of-care.plan.lifecycle',
  COMMERCIAL_PLAN_PLAN_VIEW_CLINICAL: 'commercial-plan.plan.view-clinical',
  COMMERCIAL_PLAN_PLAN_VIEW_CLIENT: 'commercial-plan.plan.view-client',
  COMMERCIAL_PLAN_PLAN_UPDATE: 'commercial-plan.plan.update',
  COMMERCIAL_PLAN_PLAN_ADVOCATE: 'commercial-plan.plan.advocate',
  COMMERCIAL_PLAN_PLAN_ASSIGN_ADVOCATE: 'commercial-plan.plan.assign-advocate',
  PLAN_OF_CARE_TEMPLATES_MANAGE: 'plan-of-care.templates.manage',
  // Orders & Prescribing
  ORDERS_SUPPLEMENTS_VIEW: 'orders.supplements.view',
  ORDERS_SUPPLEMENTS_CREATE: 'orders.supplements.create',
  ORDERS_SUPPLEMENTS_SEND: 'orders.supplements.send',
  ORDERS_LAB_ORDERS_VIEW: 'orders.lab-orders.view',
  ORDERS_LAB_ORDERS_CREATE: 'orders.lab-orders.create',
  ORDERS_LAB_ORDERS_VIEW_RESULTS: 'orders.lab-orders.view-results',
  ORDERS_LAB_ORDERS_AI_SUMMARY: 'orders.lab-orders.ai-summary',
  EPRESCRIBE_DOSESPOT_LAUNCH: 'eprescribe.dosespot.launch',
  EPRESCRIBE_DOSESPOT_PRESCRIBE: 'eprescribe.dosespot.prescribe',
  // Inventory & Dispensing
  INVENTORY_INVENTORY_VIEW: 'inventory.inventory.view',
  INVENTORY_INVENTORY_MANAGE: 'inventory.inventory.manage',
  INVENTORY_STOCK_MOVE: 'inventory.stock.move',
  INVENTORY_STOCK_ADJUST: 'inventory.stock.adjust',
  INVENTORY_DISPENSE_RECORD: 'inventory.dispense.record',
  INVENTORY_REPORTS_VIEW: 'inventory.reports.view',
  // Subscriptions
  SUBSCRIPTIONS_SUBSCRIPTION_VIEW: 'subscriptions.subscription.view',
  SUBSCRIPTIONS_SUBSCRIPTION_VIEW_STATS: 'subscriptions.subscription.view-stats',
  SUBSCRIPTIONS_SUBSCRIPTION_PAUSE: 'subscriptions.subscription.pause',
  SUBSCRIPTIONS_SUBSCRIPTION_CANCEL: 'subscriptions.subscription.cancel',
  SUBSCRIPTIONS_SUBSCRIPTION_APPROVE_PAUSE: 'subscriptions.subscription.approve-pause',
  SUBSCRIPTIONS_SUBSCRIPTION_RECONCILE: 'subscriptions.subscription.reconcile',
  SUBSCRIPTIONS_SUBSCRIPTION_MANUAL_PAYMENT: 'subscriptions.subscription.manual-payment',
  SUBSCRIPTIONS_SUBSCRIPTION_PAY_INVOICE: 'subscriptions.subscription.pay-invoice',
  SUBSCRIPTIONS_SUBSCRIPTION_WRITE_OFF: 'subscriptions.subscription.write-off',
  SUBSCRIPTIONS_SUBSCRIPTION_RESEND_NOTICE: 'subscriptions.subscription.resend-notice',
  // Payments
  PAYMENTS_PAYMENT_TAKE_CARD: 'payments.payment.take-card',
  PAYMENTS_PAYMENT_RECORD_MANUAL: 'payments.payment.record-manual',
  PAYMENTS_PAYMENT_REFUND: 'payments.payment.refund',
  PAYMENTS_PAYMENT_CHARGEBACK: 'payments.payment.chargeback',
  PAYMENTS_CARDS_VIEW: 'payments.cards.view',
  PAYMENTS_CARDS_STORE: 'payments.cards.store',
  PAYMENTS_CARDS_SET_DEFAULT: 'payments.cards.set-default',
  // Accounts Receivable
  ACCOUNTS_RECEIVABLE_LEDGER_VIEW_BALANCE: 'accounts-receivable.ledger.view-balance',
  ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER: 'accounts-receivable.ledger.view-ledger',
  ACCOUNTS_RECEIVABLE_LEDGER_POST_CHARGE: 'accounts-receivable.ledger.post-charge',
  ACCOUNTS_RECEIVABLE_LEDGER_ALLOCATE: 'accounts-receivable.ledger.allocate',
  ACCOUNTS_RECEIVABLE_LEDGER_VOID_CHARGE: 'accounts-receivable.ledger.void-charge',
  ACCOUNTS_RECEIVABLE_LEDGER_WRITE_OFF: 'accounts-receivable.ledger.write-off',
  ACCOUNTS_RECEIVABLE_DOCUMENTS_RECEIPT: 'accounts-receivable.documents.receipt',
  ACCOUNTS_RECEIVABLE_DOCUMENTS_INVOICE: 'accounts-receivable.documents.invoice',
  ACCOUNTS_RECEIVABLE_DOCUMENTS_STATEMENT: 'accounts-receivable.documents.statement',
  // Financial Reports
  REPORTS_FINANCIAL_VIEW: 'reports.financial.view',
  REPORTS_FINANCIAL_EXPORT: 'reports.financial.export',
  // Tasks
  TASKS_TASK_VIEW: 'tasks.task.view',
  TASKS_TASK_CREATE: 'tasks.task.create',
  TASKS_TASK_UPDATE: 'tasks.task.update',
  TASKS_TASK_ARCHIVE: 'tasks.task.archive',
  // Communication
  COMMUNICATION_VOICE_CALL: 'communication.voice.call',
  COMMUNICATION_CALL_LOGS_VIEW: 'communication.call-logs.view',
  // Dashboard
  DASHBOARD_DASHBOARD_VIEW: 'dashboard.dashboard.view',
} as const

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]
